import type {
    BusinessProcessResponse,
    CreateBusinessProcessParams,
    CreateProjectParams,
    ProjectDocResponse,
    ProjectModel,
    ProjectOptionModel,
    ProjectOptionResponse,
    ProjectResponse,
    ProjectUserResponse,
    UpdateBusinessProcessParams,
    UpdateProjectParams,
    UploadDocumentParams
} from '@/domain/models/project.js';
import { ProjectModel as ProjectModelClass, ProjectOptionModel as ProjectOptionModelClass } from '@/domain/models/project.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type { ProjectRepository } from '@/domain/repositories/project-repository.js';
import type { ProjectStatus } from '@/shared/utils/format-status.js';

export class ProjectRepositoryImpl implements ProjectRepository {
    constructor(private readonly httpClient: HttpClient) {}

    public async findAll(params?: { search?: string; status?: string; clientId?: string }): Promise<ProjectModel[]> {
        const qs = new URLSearchParams();
        if (params?.search) {
            qs.set('search', params.search);
        }
        if (params?.status) {
            qs.set('status', params.status);
        }
        if (params?.clientId) {
            qs.set('clientId', params.clientId);
        }
        const response = await this.httpClient.request<{ data: ProjectResponse[] }>({ url: `/api/projects?${qs}`, method: 'GET' });
        return ProjectModelClass.withFromResponseList(response.body.data);
    }

    public async findById(id: string): Promise<ProjectModel> {
        const response = await this.httpClient.request<ProjectResponse>({ url: `/api/projects/${encodeURIComponent(id)}`, method: 'GET' });
        return ProjectModelClass.withFromResponse(response.body);
    }

    public async create(params: CreateProjectParams): Promise<ProjectModel> {
        const response = await this.httpClient.request<ProjectResponse>({ url: '/api/projects', method: 'POST', body: params });
        return ProjectModelClass.withFromResponse(response.body);
    }

    public async update(id: string, params: UpdateProjectParams): Promise<ProjectModel> {
        const response = await this.httpClient.request<ProjectResponse>({ url: `/api/projects/${encodeURIComponent(id)}`, method: 'PUT', body: params });
        return ProjectModelClass.withFromResponse(response.body);
    }

    public async updateStatus(id: string, status: ProjectStatus): Promise<void> {
        await this.httpClient.request({ url: `/api/projects/${encodeURIComponent(id)}/status`, method: 'PATCH', body: { status } });
    }

    public async findMyProjects(): Promise<ProjectOptionModel[]> {
        const response = await this.httpClient.request<ProjectOptionResponse[]>({ url: '/api/projects/my', method: 'GET' });
        return ProjectOptionModelClass.withFromResponseList(response.body);
    }

    public async listBusinessProcesses(projectId: string): Promise<BusinessProcessResponse[]> {
        const response = await this.httpClient.request<BusinessProcessResponse[]>({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes`,
            method: 'GET'
        });
        return response.body;
    }

    public async listActiveBusinessProcesses(projectId: string): Promise<BusinessProcessResponse[]> {
        const response = await this.httpClient.request<BusinessProcessResponse[]>({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes/active`,
            method: 'GET'
        });
        return response.body;
    }

    public async listProjectUsers(projectId: string, profile?: string): Promise<ProjectUserResponse[]> {
        const qs = profile ? `?profile=${encodeURIComponent(profile)}` : '';
        const response = await this.httpClient.request<ProjectUserResponse[]>({
            url: `/api/projects/${encodeURIComponent(projectId)}/users${qs}`,
            method: 'GET'
        });
        return response.body;
    }

    public async createBusinessProcess(projectId: string, params: CreateBusinessProcessParams): Promise<BusinessProcessResponse> {
        const response = await this.httpClient.request<BusinessProcessResponse>({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes`,
            method: 'POST',
            body: params
        });
        return response.body;
    }

    public async updateBusinessProcess(projectId: string, processId: string, params: UpdateBusinessProcessParams): Promise<BusinessProcessResponse> {
        const response = await this.httpClient.request<BusinessProcessResponse>({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes/${encodeURIComponent(processId)}`,
            method: 'PUT',
            body: params
        });
        return response.body;
    }

    public async toggleBusinessProcess(projectId: string, processId: string, isActive: boolean): Promise<void> {
        await this.httpClient.request({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes/${encodeURIComponent(processId)}/status`,
            method: 'PATCH',
            body: { isActive }
        });
    }

    public async deleteBusinessProcess(projectId: string, processId: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/projects/${encodeURIComponent(projectId)}/business-processes/${encodeURIComponent(processId)}`,
            method: 'DELETE'
        });
    }

    public async listDocuments(projectId: string): Promise<ProjectDocResponse[]> {
        const response = await this.httpClient.request<ProjectDocResponse[]>({ url: `/api/projects/${encodeURIComponent(projectId)}/documents`, method: 'GET' });
        return response.body;
    }

    public async uploadDocument(projectId: string, params: UploadDocumentParams): Promise<ProjectDocResponse> {
        const formData = new FormData();
        formData.append('category', params.category);
        formData.append('file', params.file);
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/documents`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return data as ProjectDocResponse;
    }

    public async deleteDocument(projectId: string, docId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}`, method: 'DELETE' });
    }

    public async getDocumentDownloadUrl(projectId: string, docId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    private authHeaders(): Record<string, string> {
        const token = localStorage.getItem('accessToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
}
